import { TemplateToken, CalculatedToken, ReferenceToken, UserInputToken, SectionToken } from "./template-token";

const TOKEN_MATCHER = /{{(.*?)}}/g;

export class TokenizedConfigTemplate {
	private template: string;
	public tokens: TemplateToken[];

	/**
	 * Load and verify a template
	 * @param template Full text of an assembly config template file, with {{ }} tokens to be populated
	 */
	constructor(template: string) {
		this.template = template;
		this.tokens = this.ExtractTokens();
		this.VerifyNoDuplicates();
		this.GetIdentifiers();
	}

	/** Find all tokens in the template, parse, and validate their format */
	private ExtractTokens(): TemplateToken[] {
		const tokens: TemplateToken[] = [];
		let match: RegExpExecArray;
		let section: string;
		const lines = this.template.split("\n");
		for (let i = 0; i < lines.length; i++) {
			TOKEN_MATCHER.lastIndex = 0;
			while ((match = TOKEN_MATCHER.exec(lines[i])) !== null) {
				const token = TemplateToken.Parse(match, section, i + 1);
				if (token.type === "section") {
					section = token.description;
				}
				tokens.push(token);
			}
		}
		for (const token of tokens) {
			token.ValidateSource(tokens);
		}
		return tokens;
	}

	/** Ensure there are no non-reference tokens with duplicate ids */
	private VerifyNoDuplicates() {
		const idTrack: any = {};
		for (const token of this.tokens) {
			if (token.type !== "reference") {
				if (Object.prototype.hasOwnProperty.call(idTrack, token.id)) {
					throw new Error("Duplicate token id " + token.id + " is defined both on line " + idTrack[token.id] + " and line " + token.line);
				}
				idTrack[token.id] = token.line;
			}
		}
	}

	/** Return all user inputs, grouped by "section" */
	public GetGroupedUserInputs(): UserInputSection[] {
		const groups: UserInputSection[] = [];
		for (const token of this.tokens) {
			if (token.IsUserInput()) {
				let group = groups.find(item => item.description === token.section);
				if (!group) {
					group = {
						description: token.section,
						tokens: [],
					};
					groups.push(group);
				}
				group.tokens.push(token);
			}
		}
		return groups;
	}

	/** Verify that the required identifier tokens exist, and return their values */
	public GetIdentifiers(): AssetConfigIdentifiers {
		const type = this.tokens.find(item => item.id === "asmType");
		const engine = this.tokens.find(item => item.id === "engName");
		const gearbox = this.tokens.find(item => item.id === "gbName");
		if (!type || !engine || !gearbox) {
			throw new Error("Template must include fields for asmType, engName, and gbName");
		}
		return {
			type: type.value,
			engine: engine.value,
			gearbox: gearbox.value,
		};
	}

	/** Return a fully populated template. Assumes all user input tokens have been assigned values (or will use the default) */
	public Populate(): string {
		let populated = this.template;
		const sections = this.tokens.filter(item => item instanceof SectionToken);
		const userinputs = this.tokens.filter(item => item.IsUserInput());
		const calculateds = this.tokens.filter(item => item instanceof CalculatedToken);
		const references = this.tokens.filter(item => item instanceof ReferenceToken);
		if (sections.length + userinputs.length + calculateds.length + references.length !== this.tokens.length) {
			throw new Error("Missed tokens - one or more tokens are of the wrong type");
		}
		// Must do the tokens in order of type. References may depend on calculations, which may depend on userinputs
		for (const section of sections) {
			populated = populated.replace(section.originalText, section.Populate(this.tokens));
		}
		for (const user of userinputs) {
			populated = populated.replace(user.originalText, user.Populate(this.tokens));
		}
		for (const calc of calculateds) {
			populated = populated.replace(calc.originalText, calc.Populate(this.tokens));
		}
		for (const ref of references) {
			populated = populated.replace(ref.originalText, ref.Populate(this.tokens));
		}
		TOKEN_MATCHER.lastIndex = 0;
		if (TOKEN_MATCHER.exec(populated)) {
			throw new Error("Tokens remain after template population");
		}
		return populated;
	}
}

export interface UserInputSection {
	description: string;
	tokens: UserInputToken[];
}

export interface AssetConfigIdentifiers {
	type: string;
	engine: string;
	gearbox: string;
}
